<template>
  <div class="comment-tree pt-2">
    <CommunityCompose
      id="commentEditor"
      class="community-compose mb-4"
      @addComment="addComment"
      :user="user"
      :isLoading="isLoading"
      :hashtagsEnabled="hashtagsEnabled"
      :hashtags="hashtags"
      :allowScheduling="isStudioAdmin"
    />
    <div v-if="hasScheduledComments && isStudioAdmin" class="pl-0">
      <HMCollapse>
        <template v-slot:title>
          <span>
            {{ $t('COMMUNITY.LABEL.SCHEDULED_POSTS') }}
          </span>
          <span class="notification-icon smaller ml-1">
            {{ scheduledComments.length }}
          </span>
        </template>
        <template v-slot:content>
          <ul class="pl-0" v-if="hasScheduledComments">
            <CommunityCommentNode
              :user="user"
              :comments="scheduledComments"
              @addComment="addReply"
              @editComment="editReply"
              @onHashtag="onHashtag"
              :hashtagsEnabled="hashtagsEnabled"
              :hashtags="hashtags"
            />
          </ul>
        </template>
      </HMCollapse>
      <hr />
    </div>
    <div
      class="mt-4"
      v-infinite-scroll="loadMoreComments"
      infinite-scroll-disabled="commentsAreLoading"
      infinite-scroll-distance="10"
    >
      <ul class="pl-0" v-if="hasComments">
        <CommunityCommentNode
          :user="user"
          :comments="comments"
          @addComment="addReply"
          @editComment="editReply"
          @onHashtag="onHashtag"
          :hashtagsEnabled="hashtagsEnabled"
          :hashtags="hashtags"
        />
      </ul>
    </div>
  </div>
</template>
<script>
import CommunityCommentNode from './CommunityCommentNode';
import CommunityCompose from '@/web/components/community/CommunityCompose';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import moment from 'moment';

export default {
  name: 'CommunityCommentTree',
  props: {
    comments: Array,
    scheduledComments: Array,
    user: Object,
    users: Array,
    entityOwnerId: {
      type: String,
    },
    entityType: {
      type: String,
    },
    entityId: {
      type: Number,
    },
    additionalData: {
      type: Object,
    },
    hashtagsEnabled: {
      type: Boolean,
    },
    hashtags: {
      type: Array,
    },
    commentsAreLoading: {
      type: Boolean,
    },
  },
  data() {
    return {
      isLoading: false,
    };
  },
  components: {
    CommunityCompose,
    CommunityCommentNode,
  },
  computed: {
    ...mapGetters({
      interactAsStudio: 'community/interactAsStudio',
      teacherStudioUrl: 'user/teacherStudioUrl',
      studioUrl: 'studio/currentStudioUrl',
    }),
    hasComments() {
      return this.comments?.length > 0;
    },
    hasScheduledComments() {
      return this.scheduledComments?.length > 0;
    },
    isStudioAdmin() {
      return this.teacherStudioUrl === this.studioUrl;
    },
  },
  methods: {
    async postComment(data) {
      const parentId = data.parentId;
      const entityType = this.entityType;
      const entityId = this.entityId;
      const entityOwnerId = this.entityOwnerId;
      const content = data.content;
      const text = data.text;
      const isStudio = this.interactAsStudio;
      const additionalData = this.additionalData;
      const attachment = data?.attachment;
      const hashtag = data.hashtag;
      const releaseOn = data.releaseOn;
      const payload = {
        parentId,
        entityType,
        entityId,
        entityOwnerId,
        content,
        text,
        additionalData,
        isStudio,
        attachment,
        hashtag,
        releaseOn,
      };
      const comment = await this.$store.dispatch(
        'community/postComment',
        payload
      );
      return comment;
    },
    async editComment(data) {
      const commentId = data.id;
      const content = data.content;
      const text = data.text;
      const isStudio = this.interactAsStudio;
      const additionalData = this.additionalData;
      const attachment = data?.attachment;
      const releaseOn = data.releaseOn;
      const hashtag = data.hashtag;
      const payload = {
        commentId,
        content,
        text,
        additionalData,
        isStudio,
        attachment,
        releaseOn,
        hashtag,
      };
      const comment = await this.$store.dispatch(
        'community/editComment',
        payload
      );
      return comment;
    },
    async addComment(data) {
      this.isLoading = true;
      const payload = {
        parentId: null,
        content: data.json,
        text: data.text,
        attachment: data.attachment,
        hashtag: data.hashtag,
        releaseOn: data.releaseOn,
      };
      try {
        const comment = await this.postComment(payload);
        if (data.successFn) data.successFn();
      } catch (error) {
        if (data.errorFn) data.errorFn();
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response, `Post comment: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadMoreComments() {
      this.$emit('loadMoreComments');
    },
    async addReply(data) {
      const payload = {
        parentId: data.parentId,
        content: data.json,
        text: data.text,
        attachment: data.attachment,
      };
      try {
        const comment = await this.postComment(payload);
        if (data.successFn) data.successFn();
      } catch (error) {
        if (data.errorFn) data.errorFn();
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response, `Post comment: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    findComment(commentsList, id) {
      for (let i = 0; i < commentsList.length; i++) {
        if (commentsList[i].id === id) {
          return commentsList[i];
        }
        const found = this.findComment(commentsList[i].replies, id);
        if (found) return found;
      }
      return null;
    },
    async editReply(data) {
      const payload = {
        id: data.id,
        parentId: data.parentId,
        content: data.json,
        text: data.text,
        attachment: data.attachment,
        releaseOn: data.releaseOn,
        hashtag: data.hashtag,
      };
      try {
        const comment = await this.editComment(payload);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response, `Post comment: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    onHashtag(hashtag) {
      this.$emit('onHashtag', hashtag);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.comment-tree ul {
  padding: 0;
  margin: 0;
}

ul li {
  list-style-type: none;
}

.community-compose {
  background-color: $white;
}
</style>
